/**
 * ADR-0233 — a tiny per-variant conversion-over-time chart for the
 * Experiments view. Rendered inline as an SVG so no chart library is
 * added. Y-axis is signup conversion (signup_created / view) per
 * bucket, 0..1; X-axis is one tick per bucket. Three polylines, one
 * per variant, with the control dashed to make it visually distinct.
 *
 * When there is nothing worth plotting (every bucket is zero views for
 * every variant), the parent should hide this component — we still
 * guard here and render a quiet "No data yet" line.
 */

import type { HomeExperimentEventBucket, HomeVariant } from '@sill/types/api';

import { useC } from '../../lib/theme.js';
import { VARIANT_LABEL, VARIANT_SHORT, VARIANTS } from './experiments-helpers.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface ConversionChartProps {
  points: readonly HomeExperimentEventBucket[];
  controlVariant: HomeVariant;
  width?: number;
  height?: number;
}

export function ConversionChart({
  points,
  controlVariant,
  width = 640,
  height = 180,
}: ConversionChartProps): JSX.Element {
  const C = useC();
  const margin = { top: 8, right: 12, bottom: 24, left: 36 };
  const innerW = Math.max(10, width - margin.left - margin.right);
  const innerH = Math.max(10, height - margin.top - margin.bottom);

  const series: Record<HomeVariant, { x: number; y: number }[]> = { a: [], b: [], c: [] };
  let maxRate = 0;
  points.forEach((p, i) => {
    for (const v of VARIANTS) {
      const bucket = p[v];
      const rate = bucket.view > 0 ? bucket.signup_created / bucket.view : 0;
      if (rate > maxRate) maxRate = rate;
      series[v].push({ x: i, y: rate });
    }
  });
  // Headroom so the top line isn't flush against the frame.
  const yMax = Math.max(0.01, maxRate * 1.15);

  if (points.length === 0 || maxRate === 0) {
    return (
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12.5,
          color: C.muted,
          padding: '8px 0',
        }}
      >
        No conversion data yet.
      </div>
    );
  }

  const xStep = points.length > 1 ? innerW / (points.length - 1) : innerW;
  const toX = (i: number): number => margin.left + i * xStep;
  const toY = (rate: number): number => margin.top + innerH - (rate / yMax) * innerH;

  const COLORS: Record<HomeVariant, string> = {
    a: C.limeFg,
    b: C.skyFg,
    c: C.amberFg,
  };

  const pointsPath = (v: HomeVariant): string =>
    series[v]
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(p.x).toFixed(1)},${toY(p.y).toFixed(1)}`)
      .join(' ');

  // Three gridlines at 25/50/75% of yMax so the eye has something to
  // anchor to.
  const grid = [0.25, 0.5, 0.75, 1].map((f) => f * yMax);

  return (
    <div>
      <svg
        role="img"
        aria-label="Signup conversion over time per variant"
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height, display: 'block' }}
      >
        <rect
          x={margin.left}
          y={margin.top}
          width={innerW}
          height={innerH}
          fill="transparent"
          stroke={C.borderSoft}
        />
        {grid.map((rate) => (
          <g key={rate}>
            <line
              x1={margin.left}
              x2={margin.left + innerW}
              y1={toY(rate)}
              y2={toY(rate)}
              stroke={C.borderSoft}
              strokeDasharray="2 3"
            />
            <text
              x={margin.left - 6}
              y={toY(rate)}
              dy={3}
              textAnchor="end"
              fontFamily={monoFont}
              fontSize={9}
              fill={C.muted}
            >
              {`${Math.round(rate * 100)}%`}
            </text>
          </g>
        ))}
        {VARIANTS.map((v) => (
          <path
            key={v}
            d={pointsPath(v)}
            fill="none"
            stroke={COLORS[v]}
            strokeWidth={1.5}
            strokeDasharray={v === controlVariant ? '4 3' : undefined}
            data-testid={`chart-line-${v}`}
          />
        ))}
        {/* X-axis labels: first + last bucket only. */}
        {points.length > 0 ? (
          <>
            <text
              x={margin.left}
              y={height - 6}
              textAnchor="start"
              fontFamily={monoFont}
              fontSize={9}
              fill={C.muted}
            >
              {points[0]?.bucket ?? ''}
            </text>
            <text
              x={margin.left + innerW}
              y={height - 6}
              textAnchor="end"
              fontFamily={monoFont}
              fontSize={9}
              fill={C.muted}
            >
              {points[points.length - 1]?.bucket ?? ''}
            </text>
          </>
        ) : null}
      </svg>
      <div
        style={{
          display: 'flex',
          gap: 14,
          flexWrap: 'wrap',
          marginTop: 4,
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
        }}
      >
        {VARIANTS.map((v) => (
          <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 14,
                height: 2,
                background: COLORS[v],
                borderTop: v === controlVariant ? `1px dashed ${COLORS[v]}` : undefined,
              }}
            />
            {VARIANT_SHORT[v]} {v === controlVariant ? '(control)' : VARIANT_LABEL[v].replace(/^[A-C] — /, '')}
          </span>
        ))}
      </div>
    </div>
  );
}
